import React, { useState, useMemo, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Building2, Home, Search, LayoutList, CheckCircle, FileText, Archive } from 'lucide-react'
import {
  useListings,
  useReorderListings,
  type ListingItem,
  type ListingsReorderItem,
} from '@/hooks'

type FilterStatus = 'all' | 'draft' | 'published' | 'archived'
type FilterType = 'all' | 'building' | 'property'

/**
 * Unified listings page showing buildings and properties in a single drag-and-drop list.
 * Allows reordering across both types to control display order on landing pages.
 */
export function Viviendas() {
  const [searchParams, setSearchParams] = useSearchParams()
  const currentStatus = (searchParams.get('status') as FilterStatus) || 'all'
  const currentType = (searchParams.get('type') as FilterType) || 'all'
  const currentPage = parseInt(searchParams.get('page') || '1', 10)
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery)
  const [localListings, setLocalListings] = useState<ListingItem[] | null>(null)

  const ITEMS_PER_PAGE = 10

  // Debounce search input
  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    clearTimeout((window as unknown as { searchTimeout?: number }).searchTimeout)
    ;(window as unknown as { searchTimeout?: number }).searchTimeout = window.setTimeout(() => {
      setDebouncedSearch(value)
      if (value !== debouncedSearch) {
        searchParams.set('page', '1')
        if (value) {
          searchParams.set('search', value)
        } else {
          searchParams.delete('search')
        }
        setSearchParams(searchParams)
      }
    }, 300)
  }

  // Fetch unified listings from Convex
  const { data, isLoading, error, refetch } = useListings()
  const reorderMutation = useReorderListings()

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Use local listings during drag, otherwise use server data
  const displayData = localListings ?? data

  // Filter by status and type (client-side)
  const filteredListings = useMemo(() => {
    if (!displayData) return []

    let filtered = displayData

    // Filter by status
    if (currentStatus !== 'all') {
      filtered = filtered.filter((item: ListingItem) => item.data.status === currentStatus)
    }

    // Filter by type
    if (currentType !== 'all') {
      filtered = filtered.filter((item: ListingItem) => item.type === currentType)
    }

    // Filter by search
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase()
      filtered = filtered.filter((item: ListingItem) => {
        if (item.type === 'building') {
          return (
            item.data.name.toLowerCase().includes(searchLower) ||
            item.data.slug.toLowerCase().includes(searchLower) ||
            item.data.city?.toLowerCase().includes(searchLower) ||
            item.data.region?.toLowerCase().includes(searchLower)
          )
        } else {
          return (
            item.data.title.toLowerCase().includes(searchLower) ||
            item.data.slug.toLowerCase().includes(searchLower) ||
            item.data.city?.toLowerCase().includes(searchLower) ||
            item.data.region?.toLowerCase().includes(searchLower)
          )
        }
      })
    }

    return filtered
  }, [displayData, currentStatus, currentType, debouncedSearch])

  // Client-side pagination
  const totalItems = filteredListings.length
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const listings = filteredListings.slice(startIndex, startIndex + ITEMS_PER_PAGE)
  const hasMore = currentPage < totalPages

  // Generate unique sortable ID that includes type
  const getSortableId = (item: ListingItem) => `${item.type}-${item.data._id}`

  // Handle drag end - reorder listings
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event

      if (!over || active.id === over.id || !displayData) {
        setLocalListings(null)
        return
      }

      const oldIndex = displayData.findIndex((item: ListingItem) => getSortableId(item) === active.id)
      const newIndex = displayData.findIndex((item: ListingItem) => getSortableId(item) === over.id)

      if (oldIndex === -1 || newIndex === -1) {
        setLocalListings(null)
        return
      }

      // Optimistically update local state
      const newOrder = arrayMove(displayData, oldIndex, newIndex) as ListingItem[]
      setLocalListings(newOrder)

      // Prepare order items for API - update all items with new positions
      const orderItems: ListingsReorderItem[] = newOrder.map((item: ListingItem, index: number) => ({
        type: item.type,
        id: item.data._id,
        sort_order: index,
      }))

      try {
        await reorderMutation.mutateAsync(orderItems)
      } catch (err) {
        console.error('Failed to reorder listings:', err)
        setLocalListings(null)
      } finally {
        setLocalListings(null)
      }
    },
    [displayData, reorderMutation]
  )

  function handleStatusChange(status: FilterStatus) {
    if (status === 'all') {
      searchParams.delete('status')
    } else {
      searchParams.set('status', status)
    }
    searchParams.set('page', '1')
    setSearchParams(searchParams)
  }

  function handleTypeChange(type: FilterType) {
    if (type === 'all') {
      searchParams.delete('type')
    } else {
      searchParams.set('type', type)
    }
    searchParams.set('page', '1')
    setSearchParams(searchParams)
  }

  function handlePageChange(page: number) {
    searchParams.set('page', String(page))
    setSearchParams(searchParams)
  }

  const statusTabs: { value: FilterStatus; label: string; icon: React.ReactNode }[] = [
    { value: 'all', label: 'Todas', icon: <LayoutList className="h-3.5 w-3.5" /> },
    { value: 'published', label: 'Publicadas', icon: <CheckCircle className="h-3.5 w-3.5" /> },
    { value: 'draft', label: 'Borradores', icon: <FileText className="h-3.5 w-3.5" /> },
    { value: 'archived', label: 'Archivadas', icon: <Archive className="h-3.5 w-3.5" /> },
  ]

  const typeTabs: { value: FilterType; label: string; icon: React.ReactNode }[] = [
    { value: 'all', label: 'Todas', icon: <LayoutList className="h-3.5 w-3.5" /> },
    { value: 'building', label: 'Edificios', icon: <Building2 className="h-3.5 w-3.5" /> },
    { value: 'property', label: 'Propiedades', icon: <Home className="h-3.5 w-3.5" /> },
  ]

  // Disable drag when filtering, searching, paginating, or reordering
  const isDragDisabled = currentStatus !== 'all' || currentType !== 'all' || !!debouncedSearch || currentPage > 1 || reorderMutation.isPending

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Viviendas</h1>
            <p className="mt-2 text-muted-foreground">Ordena edificios y propiedades para la página principal</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Viviendas</h1>
            <p className="mt-2 text-muted-foreground">Ordena edificios y propiedades para la página principal</p>
          </div>
        </div>
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
          <p className="text-destructive">Error al cargar viviendas</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'Ocurrió un error'}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-4 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Intentar de nuevo
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Viviendas</h1>
          <p className="mt-2 text-muted-foreground">
            Ordena edificios y propiedades para la página principal
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border bg-card p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* Filter Groups */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
            {/* Status Filter */}
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Estado</span>
              <div className="flex gap-1 rounded-lg bg-muted/50 p-1">
                {statusTabs.map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => handleStatusChange(tab.value)}
                    className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                      currentStatus === tab.value
                        ? 'bg-background text-foreground shadow-sm ring-1 ring-border/50'
                        : 'text-muted-foreground hover:bg-background/50 hover:text-foreground'
                    }`}
                  >
                    {tab.icon}
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Type Filter */}
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Tipo</span>
              <div className="flex gap-1 rounded-lg bg-muted/50 p-1">
                {typeTabs.map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => handleTypeChange(tab.value)}
                    className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                      currentType === tab.value
                        ? 'bg-background text-foreground shadow-sm ring-1 ring-border/50'
                        : 'text-muted-foreground hover:bg-background/50 hover:text-foreground'
                    }`}
                  >
                    {tab.icon}
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="relative w-full lg:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Buscar viviendas..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full rounded-lg border border-input bg-background py-2 pl-9 pr-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>
        </div>
      </div>

      {/* Reorder hint */}
      {!isDragDisabled && listings.length > 1 && (
        <p className="text-sm text-muted-foreground">
          Arrastra las filas para reordenar las viviendas en la página principal
        </p>
      )}

      {/* Drag disabled hint */}
      {isDragDisabled && listings.length > 1 && (currentStatus !== 'all' || currentType !== 'all' || !!debouncedSearch) && (
        <p className="text-sm text-amber-600 dark:text-amber-400">
          El reordenamiento está deshabilitado mientras hay filtros activos
        </p>
      )}

      {/* Listings Table */}
      {listings.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed bg-muted/50 p-12 text-center">
          <div className="text-4xl">🏠</div>
          <h3 className="mt-4 text-lg font-semibold">
            {debouncedSearch ? 'No hay viviendas coincidentes' : 'No hay viviendas'}
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            {debouncedSearch
              ? `Ninguna vivienda coincide con "${debouncedSearch}"`
              : 'Crea edificios o propiedades para verlos aquí.'}
          </p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <div className="rounded-lg border">
            <table className="w-full">
              <thead className="border-b bg-muted/50">
                <tr>
                  {!isDragDisabled && <th className="w-10 px-2 py-3"></th>}
                  <th className="w-12 px-2 py-3 text-left text-sm font-medium">Tipo</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Nombre</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Estado</th>
                  <th className="hidden px-4 py-3 text-left text-sm font-medium md:table-cell">Ubicación</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">Acciones</th>
                </tr>
              </thead>
              <SortableContext
                items={listings.map((item: ListingItem) => getSortableId(item))}
                strategy={verticalListSortingStrategy}
                disabled={isDragDisabled}
              >
                <tbody className="divide-y">
                  {listings.map((item: ListingItem) => (
                    <SortableListingRow
                      key={getSortableId(item)}
                      item={item}
                      isDragDisabled={isDragDisabled}
                      sortableId={getSortableId(item)}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </table>
          </div>
        </DndContext>
      )}

      {/* Pagination */}
      {totalItems > ITEMS_PER_PAGE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Mostrando {startIndex + 1}-
            {Math.min(startIndex + ITEMS_PER_PAGE, totalItems)} de{' '}
            {totalItems} viviendas
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50 disabled:hover:bg-transparent"
            >
              Anterior
            </button>
            {totalPages > 1 && (
              <div className="hidden items-center gap-1 sm:flex">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum: number
                  if (totalPages <= 5) {
                    pageNum = i + 1
                  } else if (currentPage <= 3) {
                    pageNum = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i
                  } else {
                    pageNum = currentPage - 2 + i
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`min-w-[2rem] rounded-md px-2 py-1.5 text-sm ${
                        currentPage === pageNum
                          ? 'bg-primary text-primary-foreground'
                          : 'border hover:bg-muted'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}
              </div>
            )}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!hasMore}
              className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50 disabled:hover:bg-transparent"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Sortable Listing Row Component
// ============================================================================

interface SortableListingRowProps {
  item: ListingItem
  isDragDisabled: boolean
  sortableId: string
}

function SortableListingRow({
  item,
  isDragDisabled,
  sortableId,
}: SortableListingRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId, disabled: isDragDisabled })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.8 : 1,
  }

  // Get display values based on type
  const isBuilding = item.type === 'building'
  const name = isBuilding ? item.data.name : item.data.title
  const slug = item.data.slug
  const status = item.data.status
  const location = item.data.city || item.data.region || '-'
  const editPath = isBuilding
    ? `/buildings/${item.data._id}/edit`
    : `/properties/${item.data._id}/edit`

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`hover:bg-muted/50 ${isDragging ? 'bg-muted shadow-lg' : ''}`}
    >
      {/* Drag Handle */}
      {!isDragDisabled && (
        <td className="px-2 py-3">
          <button
            type="button"
            className="cursor-grab rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground active:cursor-grabbing"
            {...attributes}
            {...listeners}
            title="Arrastra para reordenar"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        </td>
      )}

      {/* Type Icon */}
      <td className="px-2 py-3">
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-md ${
            isBuilding
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
              : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
          }`}
          title={isBuilding ? 'Edificio' : 'Propiedad'}
        >
          {isBuilding ? (
            <Building2 className="h-4 w-4" />
          ) : (
            <Home className="h-4 w-4" />
          )}
        </div>
      </td>

      {/* Name */}
      <td className="px-4 py-3">
        <div className="min-w-0">
          <div className="truncate font-medium">{name}</div>
          <div className="truncate text-sm text-muted-foreground">{slug}</div>
        </div>
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <StatusBadge status={status} />
      </td>

      {/* Location */}
      <td className="hidden px-4 py-3 text-sm md:table-cell">
        {location}
      </td>

      {/* Actions */}
      <td className="px-4 py-3 text-right">
        <Link
          to={editPath}
          className="rounded-md px-2 py-1 text-sm text-primary hover:bg-muted"
        >
          Editar
        </Link>
      </td>
    </tr>
  )
}

function StatusBadge({ status }: { status: 'draft' | 'published' | 'archived' }) {
  const styles = {
    draft: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    published: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    archived: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400',
  }

  const labels: Record<string, string> = {
    draft: 'Borrador',
    published: 'Publicado',
    archived: 'Archivado',
  }

  return (
    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  )
}
